/* Product Listening Room. One media element, real waveform, native accessible seeking. */
(() => {
  if (customElements.get('kr-audio-room')) return;
  const PLAY='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4v16l13-8z"/></svg>';
  const PAUSE='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h4v16H6zM14 4h4v16h-4z"/></svg>';
  class KrosperAudio extends HTMLElement {
    connectedCallback() {
      this.dispose?.();
      const abort=new AbortController(),signal=abort.signal,q=s=>this.querySelector(s);
      const audio=q('audio'),tracks=[...this.querySelectorAll('[data-audio-track]')],play=q('[data-audio-play]'),seek=q('[data-audio-seek]'),repeat=q('[data-audio-loop]');
      if(!audio||!play||!seek)return;
      const on=(el,type,fn)=>el?.addEventListener(type,fn,{signal});
      let index=0,token=0,ready=false,disposed=false,pending=false,waveAbort=null,wavePromise=null,waveContext=null;
      const cache=new Map();
      const time=n=>Number.isFinite(n)&&n>=0?`${Math.floor(n/60)}:${String(Math.floor(n%60)).padStart(2,'0')}`:'0:00';
      const status=value=>{if(q('[data-audio-status]'))q('[data-audio-status]').textContent=value;};
      const drawWave=path=>this.querySelectorAll('[data-audio-wave]').forEach(el=>el.setAttribute('d',path));
      const sync=()=>{
        const playing=!audio.paused&&!audio.ended;
        play.innerHTML=playing?PAUSE:PLAY;
        play.setAttribute('aria-label',playing?'Pause demo':'Play demo');play.setAttribute('aria-pressed',String(playing));
        this.classList.toggle('is-playing',playing);
        tracks.forEach((track,i)=>{
          track.dataset.current=String(i===index);track.setAttribute('aria-pressed',String(i===index&&playing));
          const state=track.querySelector('.kr-audio-track-state');if(state)state.innerHTML=i===index&&playing?PAUSE:PLAY;
        });
      };
      const update=()=>{
        const duration=ready&&Number.isFinite(audio.duration)?audio.duration:0;
        const progress=duration>0?Math.max(0,Math.min(100,audio.currentTime/duration*100)):0;
        seek.disabled=duration<=0;seek.value=progress;
        q('[data-audio-time]').textContent=time(audio.currentTime);q('[data-audio-duration]').textContent=time(duration);
        seek.setAttribute('aria-valuetext',`${time(audio.currentTime)} of ${time(duration)}`);
        q('.kr-audio-timeline')?.style.setProperty('--audio-progress',progress+'%');
      };
      // Fetch only the selected demo after a user action. Native playback does not depend on decoding/CORS.
      const waveform=()=>{
        const src=tracks[index]?.dataset.src;
        if(!src||disposed)return;
        if(cache.has(src)){drawWave(cache.get(src));return;}
        if(wavePromise)return;
        const Context=window.OfflineAudioContext||window.webkitOfflineAudioContext;
        if(!Context||typeof fetch!=='function')return;
        waveAbort=new AbortController();const waveSignal=waveAbort.signal;
        wavePromise=(async()=>{
          try{
            const response=await fetch(src,{signal:waveSignal,credentials:'omit'});
            if(!response.ok)throw new Error('waveform fetch');
            // Long trailers should remain native streams; avoid unnecessary decoding memory.
            if(Number(response.headers.get('content-length'))>24000000)throw new Error('large preview');
            const buffer=await response.arrayBuffer();
            if(buffer.byteLength>24000000||waveSignal.aborted)return;
            waveContext ||= new Context(1,1,44100);
            const decoded=await waveContext.decodeAudioData(buffer);
            if(disposed||waveSignal.aborted||tracks[index]?.dataset.src!==src)return;
            const data=decoded.getChannelData(0),bins=200,stride=Math.max(1,Math.floor(data.length/bins));
            const peaks=[];let max=.001;
            for(let i=0;i<bins;i++){let peak=0;for(let j=i*stride;j<Math.min(data.length,(i+1)*stride);j+=Math.max(1,Math.floor(stride/100)))peak=Math.max(peak,Math.abs(data[j]));peaks.push(peak);max=Math.max(max,peak);}
            const path=peaks.map((peak,i)=>{const h=Math.max(.55,peak/max*24),x=(i+.5)*2.5;return `M${x.toFixed(2)} ${(28-h).toFixed(2)}v${(2*h).toFixed(2)}`;}).join('');
            if(cache.size>=5)cache.delete(cache.keys().next().value);cache.set(src,path);drawWave(path);
          }catch(error){/* A simple timeline is the honest fallback when waveform data is unavailable. */}
          finally{if(waveAbort?.signal===waveSignal)wavePromise=null;}
        })();
      };
      const start=async()=>{
        if(!tracks.length)return;const request=++token;pending=true;status('Loading demo…');waveform();
        try{await audio.play();if(!disposed&&request===token){pending=false;status('');sync();}}
        catch(error){if(!disposed&&request===token){pending=false;if(error.name!=='AbortError')status('Unable to play this demo. Please try again.');sync();}}
      };
      const load=(next,autoplay=false)=>{
        if(!tracks.length)return;
        ++token;pending=false;audio.pause();ready=false;waveAbort?.abort();wavePromise=null;status('');
        index=(next+tracks.length)%tracks.length;const track=tracks[index];
        q('[data-audio-title]').textContent=track.dataset.title||'Audio demo';q('[data-audio-subtitle]').textContent=track.dataset.subtitle||'';
        if(q('[data-audio-index]'))q('[data-audio-index]').textContent=`${String(index+1).padStart(2,'0')} / ${String(tracks.length).padStart(2,'0')}`;
        drawWave(cache.get(track.dataset.src)||'M0 28H500');
        audio.src=track.dataset.src;audio.preload='none';audio.load();update();sync();if(autoplay)start();
      };
      on(play,'click',()=>{if(pending||!audio.paused){++token;pending=false;audio.pause();status('');sync();}else start();});
      on(q('[data-audio-prev]'),'click',()=>load(index-1,true));on(q('[data-audio-next]'),'click',()=>load(index+1,true));
      tracks.forEach((track,i)=>on(track,'click',()=>{if(i===index&&(pending||!audio.paused)){++token;pending=false;audio.pause();status('');sync();}else if(i===index)start();else load(i,true);}));
      on(seek,'input',()=>{if(ready&&Number.isFinite(audio.duration)&&audio.duration>0){audio.currentTime=Math.max(0,Math.min(audio.duration,Number(seek.value)/100*audio.duration));update();}});
      on(q('[data-audio-volume]'),'input',e=>{audio.volume=Math.max(0,Math.min(1,Number(e.target.value)));});
      on(repeat,'click',()=>{audio.loop=!audio.loop;repeat.setAttribute('aria-pressed',String(audio.loop));});
      on(audio,'loadedmetadata',()=>{ready=true;update();});on(audio,'durationchange',()=>{if(Number.isFinite(audio.duration)){ready=true;update();}});
      on(audio,'timeupdate',update);on(audio,'play',sync);on(audio,'pause',sync);
      on(audio,'playing',()=>{pending=false;status('');});on(audio,'waiting',()=>{if(!audio.paused)status('Buffering…');});
      on(audio,'ended',()=>{pending=false;if(tracks.length>1&&!audio.loop)load(index+1,true);else{status('Demo finished.');sync();update();}});
      on(audio,'error',()=>{pending=false;ready=false;status('This demo is unavailable. Choose another track or try again.');sync();update();});
      on(window,'pagehide',()=>{++token;pending=false;audio.pause();waveAbort?.abort();});
      audio.volume=.8;audio.removeAttribute('controls');this.dataset.ready='true';
      if(tracks.length)load(0);else{play.disabled=true;status('Audio demos will appear here when available.');}
      [q('[data-audio-prev]'),q('[data-audio-next]')].forEach(button=>{if(button)button.disabled=tracks.length<2;});
      this.dispose=()=>{disposed=true;++token;abort.abort();waveAbort?.abort();cache.clear();waveContext=null;audio.pause();audio.removeAttribute('src');audio.querySelectorAll('source').forEach(s=>s.removeAttribute('src'));audio.load();};
    }
    disconnectedCallback(){this.dispose?.();}
  }
  customElements.define('kr-audio-room',KrosperAudio);
})();
